import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import { Building2, Save, Loader2, Mail, Phone, MapPin, Globe, Clock, Upload, Camera, X, ArrowLeft, Move } from "lucide-react";

const StoreSettingsPage = () => {
  const { user, token, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const logoInputRef = useRef(null);
  const coverInputRef = useRef(null);
  
  const [storeData, setStoreData] = useState({
    business_name: "",
    store_description: "",
    contact_email: "",
    contact_phone: "",
    address: "",
    city: "",
    postcode: "",
    website: "",
    opening_hours: "",
    logo_url: "",
    cover_image_url: "",
    cover_position: 50
  });

  useEffect(() => {
    fetchStoreData();
  }, []);

  const fetchStoreData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/merchant/store-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStoreData({
          business_name: data.business_name || user?.business_name || "",
          store_description: data.store_description || "",
          contact_email: data.contact_email || user?.email || "",
          contact_phone: data.contact_phone || "",
          address: data.address || "",
          city: data.city || "",
          postcode: data.postcode || "",
          website: data.website || "",
          opening_hours: data.opening_hours || "",
          logo_url: data.logo_url || "",
          cover_image_url: data.cover_image_url || "",
          cover_position: data.cover_position ?? 50
        });
      }
    } catch (error) {
      console.error("Error fetching store data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/merchant/store-settings`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(storeData)
      });

      if (response.ok) {
        toast.success("Store settings saved successfully!");
        if (refreshUser) refreshUser();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to save settings");
      }
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error("Please upload a JPEG, PNG, or WebP image");
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error("File size must be under 2MB");
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API}/merchant/upload/logo`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      });

      // Clone response to handle "body stream already read" issues
      let text;
      try {
        text = await response.clone().text();
      } catch {
        text = await response.text();
      }

      let data;
      try {
        data = JSON.parse(text);
      } catch {
        toast.error("Invalid response from server");
        return;
      }

      if (response.ok) {
        setStoreData(prev => ({ ...prev, logo_url: data.logo_url }));
        toast.success("Logo uploaded successfully!");
        if (refreshUser) refreshUser();
      } else {
        toast.error(data.detail || "Failed to upload logo");
      }
    } catch (error) {
      console.error("Logo upload error:", error);
      if (error.message && error.message.includes("body stream")) {
        toast.info("Logo may have uploaded - please refresh to check");
      } else {
        toast.error("Failed to upload logo - please try again");
      }
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const removeLogo = () => {
    setStoreData(prev => ({ ...prev, logo_url: "" }));
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error("Please upload a JPEG, PNG, or WebP image");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be under 5MB");
      return;
    }
    setUploadingCover(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(`${API}/merchant/upload/cover`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      let text;
      try { text = await response.clone().text(); } catch { text = await response.text(); }
      let data;
      try { data = JSON.parse(text); } catch { toast.error("Invalid response"); return; }
      if (response.ok) {
        setStoreData(prev => ({ ...prev, cover_image_url: data.cover_image_url }));
        toast.success("Cover image uploaded!");
      } else {
        toast.error(data.detail || "Failed to upload cover image");
      }
    } catch (error) {
      toast.error("Failed to upload cover image");
    } finally {
      setUploadingCover(false);
      if (coverInputRef.current) coverInputRef.current.value = '';
    }
  };

  const removeCover = () => {
    setStoreData(prev => ({ ...prev, cover_image_url: "", cover_position: 50 }));
  };

  // Cover image drag-to-reposition
  const coverContainerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ y: 0, startPos: 50 });

  const handleCoverMouseDown = useCallback((e) => {
    if (!storeData.cover_image_url) return;
    e.preventDefault();
    setIsDragging(true);
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    dragStartRef.current = { y: clientY, startPos: storeData.cover_position };
  }, [storeData.cover_image_url, storeData.cover_position]);

  const handleCoverMouseMove = useCallback((e) => {
    if (!isDragging) return;
    e.preventDefault();
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const deltaY = clientY - dragStartRef.current.y;
    const containerHeight = coverContainerRef.current?.offsetHeight || 160;
    const deltaPercent = (deltaY / containerHeight) * 100;
    const newPos = Math.max(0, Math.min(100, dragStartRef.current.startPos - deltaPercent));
    setStoreData(prev => ({ ...prev, cover_position: Math.round(newPos) }));
  }, [isDragging]);

  const handleCoverMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleCoverMouseMove);
      window.addEventListener('mouseup', handleCoverMouseUp);
      window.addEventListener('touchmove', handleCoverMouseMove, { passive: false });
      window.addEventListener('touchend', handleCoverMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleCoverMouseMove);
        window.removeEventListener('mouseup', handleCoverMouseUp);
        window.removeEventListener('touchmove', handleCoverMouseMove);
        window.removeEventListener('touchend', handleCoverMouseUp);
      };
    }
  }, [isDragging, handleCoverMouseMove, handleCoverMouseUp]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="store-settings-page">
      {/* Back Button */}
      <Link 
        to="/merchant" 
        className="inline-flex items-center gap-2 text-gray-600 hover:text-indigo-600 transition-colors"
        data-testid="back-to-dashboard-btn"
      >
        <ArrowLeft className="h-4 w-4" />
        <span>Back to Dashboard</span>
      </Link>
      
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Building2 className="h-6 w-6 text-indigo-600" />
            Store Settings
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Manage your store information and details
          </p>
        </div>
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-indigo-600 hover:bg-indigo-700"
          data-testid="save-store-settings-btn"
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </>
          )}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Basic Information */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 space-y-4">
          <h2 className="font-semibold text-slate-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">
            Basic Information
          </h2>
          
          {/* Logo Upload Section */}
          <div className="flex items-start gap-6 pb-4 border-b border-gray-100 dark:border-gray-700">
            <div className="relative">
              <div className="w-24 h-24 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600 flex items-center justify-center overflow-hidden bg-gray-50 dark:bg-gray-700">
                {storeData.logo_url ? (
                  <img 
                    src={storeData.logo_url.startsWith('data:') ? storeData.logo_url : (storeData.logo_url.startsWith('http') ? storeData.logo_url : `${API}${storeData.logo_url}`)} 
                    alt="Store Logo" 
                    className="w-full h-full object-cover"
                    data-testid="store-logo-preview"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling?.classList?.remove('hidden');
                    }}
                  />
                ) : null}
                <Camera className={`h-8 w-8 text-gray-400 ${storeData.logo_url ? 'hidden' : ''}`} />
              </div>
              {storeData.logo_url && (
                <button
                  onClick={removeLogo}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                  title="Remove logo"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <div className="flex-1 space-y-2">
              <Label className="text-gray-700 dark:text-gray-300">Store Logo</Label>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Upload a square image (300x300 recommended). Max 2MB.
              </p>
              <input
                type="file"
                ref={logoInputRef}
                onChange={handleLogoUpload}
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                data-testid="logo-file-input"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => logoInputRef.current?.click()}
                disabled={uploadingLogo}
                className="mt-2"
                data-testid="upload-logo-btn"
              >
                {uploadingLogo ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Logo
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Cover Image Upload Section */}
          <div className="pb-4 border-b border-gray-100 dark:border-gray-700">
            <Label className="text-gray-700 dark:text-gray-300 mb-3 block">Store Cover Image</Label>
            <div 
              ref={coverContainerRef}
              className={`relative rounded-xl border-2 overflow-hidden bg-gray-50 dark:bg-gray-700 ${
                storeData.cover_image_url 
                  ? isDragging ? 'border-indigo-500 cursor-grabbing' : 'border-gray-300 dark:border-gray-600 cursor-grab' 
                  : 'border-dashed border-gray-300 dark:border-gray-600'
              }`} 
              style={{ height: '180px' }}
              onMouseDown={storeData.cover_image_url ? handleCoverMouseDown : undefined}
              onTouchStart={storeData.cover_image_url ? handleCoverMouseDown : undefined}
            >
              {storeData.cover_image_url ? (
                <>
                  <img 
                    src={storeData.cover_image_url.startsWith('data:') ? storeData.cover_image_url : (storeData.cover_image_url.startsWith('http') ? storeData.cover_image_url : `${API}${storeData.cover_image_url}`)} 
                    alt="Cover" 
                    className="w-full h-full object-cover select-none pointer-events-none"
                    style={{ objectPosition: `center ${storeData.cover_position}%` }}
                    draggable={false}
                    data-testid="store-cover-preview"
                  />
                  {/* Drag hint overlay */}
                  <div className={`absolute inset-0 flex items-center justify-center transition-opacity ${isDragging ? 'opacity-0' : 'opacity-0 hover:opacity-100'}`}>
                    <div className="bg-black/60 text-white px-4 py-2 rounded-full text-sm flex items-center gap-2 shadow-lg">
                      <Move className="h-4 w-4" />
                      Drag to reposition
                    </div>
                  </div>
                  {/* Position indicator */}
                  {isDragging && (
                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/70 text-white px-3 py-1 rounded-full text-xs">
                      Position: {storeData.cover_position}%
                    </div>
                  )}
                </>
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
                  <Upload className="h-8 w-8 mb-2" />
                  <span className="text-sm">No cover image</span>
                </div>
              )}
              {storeData.cover_image_url && (
                <button
                  onClick={(e) => { e.stopPropagation(); removeCover(); }}
                  className="absolute top-2 right-2 w-7 h-7 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors shadow-lg z-10"
                  title="Remove cover"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <div className="flex items-center gap-3 mt-3">
              <input
                type="file"
                ref={coverInputRef}
                onChange={handleCoverUpload}
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                data-testid="cover-file-input"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => coverInputRef.current?.click()}
                disabled={uploadingCover}
                data-testid="upload-cover-btn"
              >
                {uploadingCover ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    {storeData.cover_image_url ? 'Change Cover Image' : 'Upload Cover Image'}
                  </>
                )}
              </Button>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {storeData.cover_image_url ? 'Drag image to reposition. ' : ''}Recommended: 1920x600px. Max 5MB.
              </p>
            </div>
          </div>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="business_name" className="text-gray-700 dark:text-gray-300">
                Store / Business Name *
              </Label>
              <Input
                id="business_name"
                value={storeData.business_name}
                onChange={(e) => setStoreData({ ...storeData, business_name: e.target.value })}
                placeholder="Your Store Name"
                className="mt-1"
                data-testid="store-name-input"
              />
            </div>

            <div>
              <Label htmlFor="store_description" className="text-gray-700 dark:text-gray-300">
                Store Description
              </Label>
              <Textarea
                id="store_description"
                value={storeData.store_description}
                onChange={(e) => setStoreData({ ...storeData, store_description: e.target.value })}
                placeholder="Tell customers about your store..."
                className="mt-1 min-h-[100px]"
                data-testid="store-description-input"
              />
            </div>

            <div>
              <Label htmlFor="website" className="text-gray-700 dark:text-gray-300">
                <Globe className="h-4 w-4 inline mr-1" />
                Website
              </Label>
              <Input
                id="website"
                value={storeData.website}
                onChange={(e) => setStoreData({ ...storeData, website: e.target.value })}
                placeholder="https://www.yourstore.com"
                className="mt-1"
                data-testid="store-website-input"
              />
            </div>

            <div>
              <Label htmlFor="opening_hours" className="text-gray-700 dark:text-gray-300">
                <Clock className="h-4 w-4 inline mr-1" />
                Opening Hours
              </Label>
              <Input
                id="opening_hours"
                value={storeData.opening_hours}
                onChange={(e) => setStoreData({ ...storeData, opening_hours: e.target.value })}
                placeholder="Mon-Fri: 9am-6pm, Sat: 10am-4pm"
                className="mt-1"
                data-testid="store-hours-input"
              />
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 space-y-4">
          <h2 className="font-semibold text-slate-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">
            Contact Information
          </h2>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="contact_email" className="text-gray-700 dark:text-gray-300">
                <Mail className="h-4 w-4 inline mr-1" />
                Contact Email
              </Label>
              <Input
                id="contact_email"
                type="email"
                value={storeData.contact_email}
                onChange={(e) => setStoreData({ ...storeData, contact_email: e.target.value })}
                placeholder="contact@yourstore.com"
                className="mt-1"
                data-testid="store-email-input"
              />
            </div>

            <div>
              <Label htmlFor="contact_phone" className="text-gray-700 dark:text-gray-300">
                <Phone className="h-4 w-4 inline mr-1" />
                Contact Phone
              </Label>
              <Input
                id="contact_phone"
                value={storeData.contact_phone}
                onChange={(e) => setStoreData({ ...storeData, contact_phone: e.target.value })}
                placeholder="+44 20 1234 5678"
                className="mt-1"
                data-testid="store-phone-input"
              />
            </div>

            <div>
              <Label htmlFor="address" className="text-gray-700 dark:text-gray-300">
                <MapPin className="h-4 w-4 inline mr-1" />
                Address
              </Label>
              <Input
                id="address"
                value={storeData.address}
                onChange={(e) => setStoreData({ ...storeData, address: e.target.value })}
                placeholder="123 High Street"
                className="mt-1"
                data-testid="store-address-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="city" className="text-gray-700 dark:text-gray-300">City</Label>
                <Input
                  id="city"
                  value={storeData.city}
                  onChange={(e) => setStoreData({ ...storeData, city: e.target.value })}
                  placeholder="London"
                  className="mt-1"
                  data-testid="store-city-input"
                />
              </div>
              <div>
                <Label htmlFor="postcode" className="text-gray-700 dark:text-gray-300">Postcode</Label>
                <Input
                  id="postcode"
                  value={storeData.postcode}
                  onChange={(e) => setStoreData({ ...storeData, postcode: e.target.value })}
                  placeholder="SW1A 1AA"
                  className="mt-1"
                  data-testid="store-postcode-input"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-xl p-4">
        <p className="text-sm text-indigo-700 dark:text-indigo-300">
          <strong>Note:</strong> Your store information is visible to customers and will be displayed on your public store page. 
          Changes are reflected immediately in the Admin panel under Merchant Details.
        </p>
      </div>
    </div>
  );
};

export default StoreSettingsPage;
